import { Pannellum } from "../components/Pannellum";
import { useState, useEffect, useRef } from "react";
import { Toaster } from "react-hot-toast";
import "react-slideshow-image/dist/styles.css";
import "../style/LoadingPage.css";
import "../style/webtour.css";
import ZOOM_IN_ICON from "../images/plus.svg";
import ZOOM_OUT_ICON from "../images/minus.svg";
import FULL_IN_ICON from "../images/fullin.svg";
import FULL_OUT_ICON from "../images/fullout.svg";
import UP_ARROR_ICON from "../images/up-arrow-white.png";
import Tour from "reactour";
import SQUARE_LOADER_IMG from "../images/square-loading.gif";
import Header from "../components/header/Header";
import useFullscreen from "../hooks/useFullScreen";

const asyncTimeout = (ms) => {
  // when you make a promise you have to resolve it or reject it
  // if you are like me that didn't get promises at all read the docs
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      resolve(ms); // this returns users
    }, ms);
  });
};

export default function WebTour({ param }) {
  const [scenes, setScenes] = useState({});
  const [scene, setScene] = useState(null);
  const [loading, setLoading] = useState(true);
  const [GRID, setGRID] = useState(null);
  const [planDetails, setPlanDetails] = useState(null);
  const [nameMap, setNameMap] = useState({});
  const [designID, setDesignID] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);
  const [thumbnailMap, setThumbnailMap] = useState({});
  const [progress, setProgress] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(0);
  const [floorList, setFloorList] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [inProgressImage, setInProgressImage] = useState();
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [isFullscreen, setFullscreen] = useFullscreen();
  const [prevState, setPrevState] = useState(null);
  const [scenesEntries, setScenesEntries] = useState(null);
  const [floorPlans, setFloorPlans] = useState(null);

  const accentColor = "#b84446";
  const defaultFloor = "Main Floor"; // TODO: should be from BE

  const fetchGrid = async () => {
    try {
      const queryParams = new URLSearchParams(window.location.search);

      let floorIDValue;
      if (queryParams.has("floorplan")) {
        floorIDValue = queryParams.get("floorplan");
      } else {
        console.log("Floor ID parameter does not exist");
      }

      let designIDValue;
      if (queryParams.has("design_id")) {
        designIDValue = queryParams.get("design_id");
      } else {
        console.log("Design ID parameter does not exist");
      }

      if (queryParams.has("floorplan")) {
        const url = `${process.env.REACT_APP_VIZ_API_URL}/web-tour/rooms?designId=${designIDValue}`;

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const grids = await response.json();
        grids.defaultRooms = [];
        let map = {};
        grids.rooms.forEach((r) => {
          if (r.name && r.displayName) {
            map[r.name] = r.displayName;
          }
          r.displayName = r.displayName || r.name;
          r.webtourDefaultRoom &&
            grids.defaultRooms.push({
              floorName: r.floorName || defaultFloor,
              grid: r.grid,
            });
        });
        setNameMap(map);
        if (grids.linkedWebTours && grids.linkedWebTours.length > 0) {
          const defaultWebtour = grids.linkedWebTours.find(
            (wt) => !!wt.defaultInGroup
          );
          if (defaultWebtour) {
            setDesignID(defaultWebtour.designId);
          } else {
            setDesignID(designIDValue);
          }
        } else {
          setDesignID(designIDValue);
        }
        setGRID(grids);

        fetchFloorDiagram(floorIDValue);
      } else {
        console.log("Floorplan parameter does not exist");
      }
    } catch (error) {
      console.error("Error fetching Grids:", error);
      throw error;
    }
  };

  const getBaseUrl = (grids) => {
    return `${process.env.REACT_APP_S3_DOMAIN}/${
      queryParams.get("context") === "qa"
        ? process.env.REACT_APP_S3_DIR_PATH_QA
        : process.env.REACT_APP_S3_DIR_PATH
    }/${grids.clientGrid}/${grids.communityGrid}/${grids.grid}/${designID}`;
  };

  const fetchData = async (grids) => {
    const baseUrl = getBaseUrl(grids);
    const url = `${baseUrl}/${grids.clientGrid}_${grids.communityGrid}.json`;

    // const s3Params = { Bucket: "rndtest", Key: s3JsonKey };

    try {
      const data = await fetch(url);
      const parsedData = await data.json();
      return parsedData;
    } catch (error) {
      console.error("Error fetching file from S3:", error);
      throw error;
    }
  };

  const startLoading = (image, onProgress, onComplete) => {
    onProgress = onProgress || (() => {});
    onComplete = onComplete || (() => {});
    const xhr = new XMLHttpRequest();
    xhr.open("GET", image, true);
    xhr.responseType = "blob";

    xhr.onprogress = function (event) {
      if (event.lengthComputable) {
        const percentComplete = (event.loaded / event.total) * 100;
        onProgress(Math.round(percentComplete));
      }
    };

    xhr.onload = function () {
      if (xhr.status === 200) {
        const blob = xhr.response;
        const imageUrl = URL.createObjectURL(blob);
        onProgress(100);
        onComplete(imageUrl, "success");
      } else {
        console.error("Failed to load image");
        onComplete(null, "error");
      }
    };

    xhr.onerror = function () {
      console.error("Error occurred while loading the image");
      onComplete(null, "error");
    };

    xhr.send();
    return xhr;
  };

  const loads3Image = async (grids, mainKey, isFirst) => {
    const url = `${getBaseUrl(grids)}/${mainKey}.jpg`;
    return new Promise((resolve, reject) => {
      startLoading(
        url,
        isFirst
          ? (progress) => {
              try {
                document.getElementById("progress").style.width =
                  progress + "%";
              } catch (e) {}
            }
          : null,
        (data, status) => {
          if (status === "error") {
            reject();
          } else {
            resolve(data);
          }
        }
      );
    });
  };

  const recursionScene = (data, key, array) => {
    if (!array.includes(key)) {
      array.push(key);
    } else {
      return array;
    }
    let scene = data[key];
    if (!scene) {
      return array;
    }
    let keys = Object.keys(scene).filter((k) => !!data[k]);
    for (let i = 0; i < keys.length; i++) {
      recursionScene(data, keys[i], array);
    }
    return array;
  };

  const sortScenes = (data, startingKey) => {
    const newEntries = [];
    recursionScene(data, startingKey, newEntries);
    return newEntries;
  };

  const handleImages = (
    image,
    mainIndex,
    totalScenes,
    newScenes,
    sceneKey,
    mainKey
  ) => {
    try {
      setProgress(Math.floor((mainIndex * 100) / totalScenes));
      newScenes[sceneKey].image = image;
      setScenes({ ...newScenes });
      generateThumbnail(image, mainKey);
    } catch (error) {
      console.error(`Error fetching image from S3: ${error}`);
      throw error;
    }
  };

  const fetchFloorDiagram = async (floorPlanId) => {
    const adminUrl = `${process.env.REACT_APP_API_BASE_URI}/admin/api/v1/floorplans/?floorPlanGrid=${floorPlanId}`;
    fetch(adminUrl).then((resp) => {
      resp.json().then((data) => {
        setPlanDetails(data);
      });
    });

    const url = `${process.env.REACT_APP_API_BASE_URI}/viz2/api/v1/web-tour/floorplan-diagrams/${floorPlanId}`;
    fetch(url).then((resp) => {
      resp.json().then((data) => {
        if (data?.length) {
          setFloorList(
            data.map((d) => ({
              floorName: d.name,
              displayName: d.name,
            }))
          );
          setSelectedFloor(data[0].name);
        }
        setFloorPlans(data);
      });
    });
  };

  const fetchImagesFromS3 = async (grids, defaultRoomGrid, designId) => {
    let scenesData;
    if (!scenesEntries) {
      const data = await fetchData(grids);
      scenesData = Object.fromEntries(
        Object.entries(data || {}).sort(([a], [b]) => a.localeCompare(b))
      );
      setScenesEntries(scenesData);
    } else {
      scenesData = scenesEntries;
    }
    const newScenes = {};
    const totalScenes = Object.keys(scenesData).length;
    defaultRoomGrid =
      defaultRoomGrid ||
      GRID.defaultRooms?.find(
        (r) => r.floorName?.toLowerCase() === defaultFloor.toLowerCase()
      )?.grid ||
      GRID.defaultRooms?.find(
        (r) => r.floorName?.toLowerCase() === "First Floor".toLowerCase()
      )?.grid;
    const entries = Object.keys(scenesData);
    const targetEntry = entries.find((mainKey, mainIndex) => {
      return defaultRoomGrid
        ? mainKey.indexOf(defaultRoomGrid) === 0
        : mainIndex === 0;
    });

    const mainKey = targetEntry;
    const newEntries = sortScenes(scenesData, mainKey);
    entries.forEach((mainKey, mainIndex) => {
      if (!newEntries.includes(mainKey)) {
        newEntries.push(mainKey);
      }
    });

    const cameraNumber = newEntries.findIndex((e) => e === targetEntry) + 1;
    const initialScene = `image${cameraNumber}`;
    const hotSpotData = scenesData[mainKey];
    const StartingPitch = hotSpotData.StartingPitch;
    const initialSceneObj = {
      renderKey: mainKey,
      image: "",
      cameraNumber,
      hotSpot: { scene: initialScene },
      hotSpotData,
      StartingPitch,
    };
    const [imageUrl, time] = await Promise.all([
      loads3Image(grids, mainKey, true),
      asyncTimeout(localStorage.getItem("tv") ? 0 : 6000),
    ]);
    initialSceneObj.image = imageUrl;

    for (let mainIndex = 0; mainIndex < newEntries.length; mainIndex++) {
      const mainKey = newEntries[mainIndex];
      if (scenesData.hasOwnProperty(mainKey)) {
        const cameraNumber = mainIndex + 1;
        const sceneKey = `image${mainIndex + 1}`;

        try {
          const hotSpotData = scenesData[mainKey];
          const StartingPitch = hotSpotData.StartingPitch;
          delete hotSpotData.StartingPitch;
          const renderKey = mainKey;
          newScenes[sceneKey] = {
            renderKey,
            image: "",
            cameraNumber,
            hotSpot: { scene: sceneKey },
            hotSpotData,
            StartingPitch,
          };
        } catch (error) {
          console.error(`Error fetching image from S3: ${error}`);
          throw error;
        }
      }
    }

    setScenes(newScenes);
    setScene(initialSceneObj);
    setTimeout(() => {
      setLoading(false);
      GRID.linkedWebTours?.length > 1 && setOpenMenu("compare");
    }, 500);
    if (!localStorage.getItem("tv")) {
      localStorage.setItem("tv", "true");
    }

    for (let mainIndex = 0; mainIndex < newEntries.length; mainIndex++) {
      const mainKey = newEntries[mainIndex];
      const sceneKey = `image${mainIndex + 1}`;

      loads3Image(grids, mainKey).then((data) => {
        handleImages(
          data,
          mainIndex,
          totalScenes,
          newScenes,
          sceneKey,
          mainKey
        );
      });
    }

    return newScenes;
  };

  useEffect(() => {
    !GRID && fetchGrid();
    setTimeout(() => {
      setIsTourOpen(true);
    }, 500);
  }, [GRID]);

  useEffect(() => {
    if (scenes) {
      // load image once loaded if user has already clicked
      if (inProgressImage) {
        const scene = Object.values(scenes).find(
          (s) => s.renderKey === inProgressImage[0].renderKey
        );
        if (scene?.image) {
          setScene(scene);
          if (inProgressImage[1] && inProgressImage[1].floorName) {
            setSelectedFloor(inProgressImage[1].floorName);
          }
          setInProgressImage(null);
        }
      }
    }
  }, [scenes, inProgressImage]);

  useEffect(() => {
    if (!loading || !tourRef || !isTourOpen) {
      return;
    }
    setTimeout(() => {
      let slideNo = currentSlide + 1;
      if (slideNo > 3) {
        slideNo = 0;
      }
      tourRef?.current?.gotoStep(slideNo);
      setCurrentSlide(slideNo);
    }, 1700);
  }, [loading, currentSlide, isTourOpen]);

  useEffect(() => {
    GRID &&
      designID &&
      fetchImagesFromS3(
        GRID,
        scene ? getRoomInfo(scene?.renderKey)?.room?.grid : "",
        scene ? designID : ""
      ).catch((error) => {
        console.error("Error:", error);
      });
  }, [designID, GRID]);

  const getImageScene = (index) => {
    const key = `image${index}`;
    return scenes[key];
  };

  const getMainKeyFromRenderKey = (renderKey) => {
    const mainKey = Object.keys(scenes).find(
      (key) => scenes[key].renderKey === renderKey
    );
    return mainKey;
  };

  const getRoomInfo = (renderKey) => {
    // Special Case for failed Waypoints
    if (renderKey.includes("Pano")) {
      return 1;
    }
    const mainKey = Object.keys(scenes).find(
      (key) => scenes[key].renderKey === renderKey
    );
    const roomId = renderKey.split("_")[0];
    return {
      cameraNumber: scenes[mainKey].cameraNumber,
      room: GRID?.rooms?.find((r) => r.grid === roomId) || {
        grid: roomId,
        name: "Unknown Room",
        floorName: null,
        displayName: "Unknown Room",
      },
    };
  };

  const hotSpotIcon = (hotSpotDiv, roomInfo) => {
    const span = document.createElement("span");
    if (roomInfo?.room?.name?.toLowerCase().includes("stair")) {
      const img = document.createElement("img");
      img.src = UP_ARROR_ICON;
      span.appendChild(img);
      span.classList.add("stairs");
      // if (roomInfo.room.floorName < selectedFloor) {
      //   span.classList.add("down");
      // }
    } else {
      span.classList.add("icon");
    }
    span.classList.add("waypoint");
    hotSpotDiv.appendChild(span);
    const tooltip = document.createElement("div");
    tooltip.classList.add("hotspotTip");
    tooltip.innerHTML = `${roomInfo?.room?.displayName || ""}`;
    hotSpotDiv.appendChild(tooltip);
  };

  const generateThumbnail = (imageUrl, subKey) => {
    const image = new Image();
    image.crossOrigin = "Anonymous";
    image.src = imageUrl;

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      // Note for viz dev - make sure you keep the same ratio or else image will be streched.
      // You can try out other ratios if required

      const desiredWidth = 550;
      const desiredHeight = 350;

      const aspectRatio = image.width / image.height;

      let scaledWidth = desiredWidth * 5.0;
      let scaledHeight = (desiredWidth / aspectRatio) * 5.0;

      const offsetX = (desiredWidth - scaledWidth) / 2;
      const offsetY = (desiredHeight - scaledHeight) / 2;

      canvas.width = desiredWidth;
      canvas.height = desiredHeight;
      context.drawImage(image, offsetX, offsetY, scaledWidth, scaledHeight);

      const thumbnail = canvas.toDataURL("image/jpeg");
      setThumbnailMap((prev) => {
        return { ...prev, [subKey]: thumbnail };
      });
    };
  };

  const instance = useRef();

  const setZoom = (diff) => {
    let newHfov = instance.current.panorama.getHfov() + diff;
    newHfov = Math.min(100, newHfov);
    newHfov = Math.max(50, newHfov);
    instance.current.panorama.setHfov(newHfov);
  };

  const handleOnLoad = () => {
    if (prevState) {
      instance.current.panorama.lookAt(
        prevState.pitch,
        prevState.yaw,
        prevState.hfov,
        false
      );
      setPrevState(null);
    }
    const hfov = 150 - (instance?.current?.panorama?.getHfov() || 0);
    setZoomLevel(Math.round(hfov));
  };

  const handleFullScreen = () => {
    setFullscreen(!isFullscreen);
  };

  const handleLoading = (scene, floor) => {
    if (inProgressImage) {
      return;
    }

    const roomInfo = getRoomInfo(scene.renderKey);
    setInProgressImage([scene, floor, roomInfo]);
  };

  const handleDesignSelect = (design) => {
    setDesignID(design.designId);
    setPrevState({
      yaw: instance.current.panorama.getYaw(),
      pitch: instance.current.panorama.getPitch(),
      hfov: instance.current.panorama.getHfov(),
    });
  };

  const handleFloorSelection = (floor) => {
    let defaultRoom =
      GRID.defaultRooms?.find((r) => r.floorName === floor.floorName)?.grid ||
      floor.grid;
    const sceneKeys = Object.keys(scenes);
    let firstFloorRoomKey = sceneKeys.find((sceneKey) => {
      return scenes[sceneKey].renderKey.indexOf(defaultRoom) === 0;
    });
    if (!firstFloorRoomKey) {
      firstFloorRoomKey = sceneKeys.find((sceneKey) => {
        const sceneObj = scenes[sceneKey];
        const key = sceneObj.renderKey;

        if (!key || !sceneObj) {
          return false;
        }

        const roomInfo = getRoomInfo(key);
        const selectedFloor = floor.floorName;
        if (
          roomInfo?.room?.floorName &&
          roomInfo?.room?.floorName?.toLowerCase() ===
            selectedFloor?.toLowerCase()
        ) {
          return true;
        }
        return false;
      });
    }
    if (firstFloorRoomKey) {
      const roomInfo = getRoomInfo(scenes[firstFloorRoomKey].renderKey);
      const scene = getImageScene(roomInfo.cameraNumber);
      if (!scene?.image || inProgressImage) {
        handleLoading(scene, floor);
        return;
      }
      setScene(scene);
    }
    floor.floorName && setSelectedFloor(floor.floorName);
    setOpenMenu("");
  };

  const handleRoomSelectionFromDiagram = (room) => {
    const scene = Object.values(scenes).find((scene) =>
      scene.renderKey.includes(room.roomGrid)
    );
    if (!scene) {
      return;
    }
    setOpenMenu("");
    if (!scene?.image || inProgressImage) {
      handleLoading(scene);
      return;
    }
    setScene(scene);
  };

  const queryParams = new URLSearchParams(window.location.search);

  const closeTour = () => {
    setIsTourOpen(false);
  };

  const tourConfig = [
    {
      selector: '[data-tour="reactour__floor"]',
      content: () => (
        <div>
          <h2>Switch Floor</h2>
          <p className="tour-content">
            Use the dropdown to switch between floors easily.
          </p>
        </div>
      ),
      position: "bottom",
      observe: '[data-tour="observable-parent"]',
    },
    {
      selector: '[data-tour="reactour__room"]',
      content: () => (
        <div>
          <h2>Navigate Rooms</h2>
          <p className="tour-content">
            Use the dropdown to navigate to different rooms.
          </p>
        </div>
      ),
      position: "bottom",
      observe: '[data-tour="observable-parent"]',
    },
    // {
    //   selector: '[data-tour="reactour__explore"]',
    //   content: () => (
    //     <div>
    //       <h2>Explore Designs</h2>
    //       <p className="tour-content">
    //         View similar design and Complare to select
    //       </p>
    //     </div>
    //   ),
    //   position: "bottom",
    //   observe: '[data-tour="observable-parent"]',
    // },
    {
      selector: '[data-tour="reactour__plan"]',
      content: () => (
        <div>
          <h2>Floor Plan Details</h2>
          <p className="tour-content">View the Floor Plan for this home.</p>
        </div>
      ),
      position: "bottom",
      observe: '[data-tour="observable-parent"]',
    },
    {
      selector: '[data-tour="reactour__waypoint"]',
      content: () => (
        <div>
          <h2>Enter a Room</h2>
          <p className="tour-content">
            Click the circle button to enter a specific room.
          </p>
        </div>
      ),
      position: "right",
      observe: '[data-tour="observable-parent"]',
    },
  ];

  const tourRef = useRef();

  return (
    <>
      <Header
        loaded={!loading}
        selectedDesignId={designID}
        selectedFloor={selectedFloor}
        selectedRoom={scene ? getRoomInfo(scene?.renderKey)?.room?.grid : ""}
        selectedRoomName={
          scene ? getRoomInfo(scene?.renderKey)?.room?.displayName : ""
        }
        openMenu={openMenu}
        floorList={floorList}
        thumbnailMap={thumbnailMap}
        getRoomInfo={getRoomInfo}
        getImageScene={getImageScene}
        scenes={scenes}
        scene={scene}
        grid={GRID}
        setScene={setScene}
        inProgressImage={inProgressImage}
        onRoomSelect={(e) => {
          handleRoomSelectionFromDiagram(e);
        }}
        setOpenMenu={setOpenMenu}
        handleFloorSelection={handleFloorSelection}
        setSelectedFloor={setSelectedFloor}
        floorPlans={floorPlans}
        onDesignSelect={handleDesignSelect}
        handleLoading={handleLoading}
        fullScreen={isFullscreen}
        plan={planDetails}
        nameMap={nameMap}
      />
      <Toaster />
      {loading && (
        <div
          className="slide-container"
          style={{
            height: "calc(100vh - 72px)",
            marginTop: "72px",
            backgroundImage: GRID?.image ? `url(${GRID?.image})` : "",
            backgroundColor: "#f1f1f1",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            position: "relative",
          }}
        >
          <div className="progress-bar">
            <div
              id="progress"
              className="progress-box"
              style={{ width: 0 }}
            ></div>
            Explore your new home at {GRID?.communityName}
          </div>
          <div
            style={{
              bottom: "60px",
              right: "95px",
              top: "auto",
              visibility: "visible",
            }}
            className="pnlm-hotspot-base custom-hotspot pnlm-pointer"
          >
            <span className="icon waypoint"></span>
            <div className="hotspotTip">Room</div>
          </div>
          <div
            style={{ top: "50%", left: "30%", visibility: "visible" }}
            className="pnlm-hotspot-base custom-hotspot pnlm-pointer"
            data-tour="reactour__waypoint"
          >
            <span className="icon waypoint"></span>
            <div className="hotspotTip">Room</div>
          </div>
          <Tour
            ref={tourRef}
            onRequestClose={closeTour}
            steps={tourConfig}
            offset
            isOpen={isTourOpen}
            maskClassName="mask"
            className="helper"
            rounded={12}
            accentColor={accentColor}
            closeWithMask={false}
            disableDotsNavigation={true}
            disableInteraction={true}
            disableKeyboardNavigation={true}
            showCloseButton={false}
            showButtons={false}
            showNavigation={false}
            showNavigationNumber={false}
            showNumber={false}
          />
        </div>
      )}
      <div
        style={{
          height: isFullscreen ? "100vh" : "calc(100vh - 72px)",
          marginTop: isFullscreen ? "0" : "72px",
          position: "relative",
        }}
      >
        {inProgressImage && (
          <div className="square-loading">
            <span>
              <i
                style={{
                  background: `url(${SQUARE_LOADER_IMG}) center no-repeat`,
                }}
              ></i>
              Entering {inProgressImage[2].room.displayName || ""}
            </span>
          </div>
        )}
        {scene && (
          <Pannellum
            ref={instance}
            width={"100%"}
            height={"100%"}
            image={scene.image}
            pitch={scene.StartingPitch || 0.28}
            yaw={0}
            autoLoad
            showControls={false}
            orientationOnByDefault={true}
            hfov={100}
            minHfov={50}
            maxHfov={100}
            hotSpotSize={70}
            loadingDisplay={false}
            showZoomCtrl={false}
            mouseZoom={false}
            showFullscreenCtrl={false}
            keyboardZoom={false}
            disableKeyboardCtrl={true}
            minPitch={-70}
            maxPitch={70}
            sceneFadeDuration={1000}
            onLoad={handleOnLoad}
            onRender={handleOnLoad}
          >
            {Object.keys(scene.hotSpotData).map((key) => {
              const hotspotName = getMainKeyFromRenderKey(key);
              if (!hotspotName) {
                return <></>;
              }
              const sceneObj = scene.hotSpotData[key];
              const roomInfo = getRoomInfo(key);
              return (
                <Pannellum.Hotspot
                  type="custom"
                  cssClass="custom-hotspot"
                  key={key}
                  pitch={sceneObj.Pitch}
                  yaw={sceneObj.Yaw}
                  name={hotspotName}
                  tooltip={(el) => hotSpotIcon(el, roomInfo)}
                  handleClick={(evt, name) => {
                    const scene = scenes[hotspotName];
                    if (!scene?.image || inProgressImage) {
                      handleLoading(scene, roomInfo.room);
                      return;
                    }
                    roomInfo.room.floorName &&
                      setSelectedFloor(roomInfo.room.floorName);
                    setScene(scene);
                  }}
                >
                  T
                </Pannellum.Hotspot>
              );
            })}
          </Pannellum>
        )}
        <div
          style={{
            position: "absolute",
            bottom: "10px",
            right: "10px",
            width: "auto",
            height: 40,
            textAlign: "center",
            display: "flex",
            gap: "15px",
            zIndex: 1,
            ...(loading ? { pointerEvents: "none" } : {}),
          }}
        >
          <div
            style={{
              textAlign: "center",
              color: "black",
              padding: 0,
              cursor: "pointer",
              backgroundColor: "rgba(255,255,255,1)",
              marginRight: "5px",
              borderRadius: "4px",
              position: "relative",
              display: isFullscreen ? "none" : "flex",
            }}
          >
            <span
              style={{
                flex: 1,
                display: "inline-flex",
                justifyContent: "center",
                padding: 8,
              }}
              onClick={() => {
                setZoom(8);
              }}
            >
              <img src={ZOOM_OUT_ICON} alt="Zoom out" />
            </span>
            <span
              style={{
                flex: 1,
                display: "inline-flex",
                justifyContent: "center",
                alignItems: "center",
                padding: 10,
              }}
            >
              {zoomLevel}%
            </span>
            <span
              style={{
                flex: 1,
                display: "inline-flex",
                justifyContent: "center",
                padding: 8,
              }}
              onClick={() => {
                setZoom(-8);
              }}
            >
              <img src={ZOOM_IN_ICON} alt="Zoom in" />
            </span>
          </div>
          <div
            style={{
              textAlign: "center",
              color: "black",
              padding: 0,
              cursor: "pointer",
              backgroundColor: "rgba(255,255,255,1)",
              marginRight: "5px",
              borderRadius: "4px",
              position: "relative",
              display: "flex",
            }}
          >
            <span
              style={{
                flex: 1,
                display: "inline-flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "7px",
                padding: 10,
              }}
              onClick={() => {
                handleFullScreen();
              }}
            >
              {isFullscreen ? (
                <img src={FULL_OUT_ICON} alt="Fullscreen out" />
              ) : (
                <img src={FULL_IN_ICON} alt="Fullscreen in" />
              )}
              {isFullscreen ? "Minimize" : "Full Screen"}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
